import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'result',
    'resultWrapper', 
    'submitBtn',
    'gameHeadersRow',
    'buttonContainer'
  ];
  static values = { maxGames: Number, defaultWinnerScore: { type: Number, default: 11 }, defaultLoserScore: { type: Number, default: 7 } };

  connect() {
    // Hide submit button initially
    this.submitBtnTarget.classList.add('d-none');
    
    // Populate result options based on maxGames
    this.populateResultOptions();
  }

  populateResultOptions() {
    const resultSelect = this.resultTarget;
    const challengerName = resultSelect.dataset.challengerName;
    const challengedName = resultSelect.dataset.challengedName;
    
    // Clear existing options except the first one
    while (resultSelect.options.length > 1) {
      resultSelect.remove(1);
    }
    
    // Use maxGames value if available, default to 5 (best of 5)
    const maxGames = this.hasMaxGamesValue ? this.maxGamesValue : 5;
    const minGames = Math.ceil((maxGames + 1) / 2); // Games needed to win
    
    // Add challenger win options
    for (let totalGames = minGames; totalGames <= maxGames; totalGames++) {
      const loserGames = totalGames - minGames;
      const option = new Option(
        `${challengerName} ${minGames}-${loserGames}`,
        `A${totalGames}`
      );
      resultSelect.add(option);
    }
    
    // Add challenged win options
    for (let totalGames = minGames; totalGames <= maxGames; totalGames++) {
      const loserGames = totalGames - minGames;
      const option = new Option(
        `${challengedName} ${minGames}-${loserGames}`,
        `B${totalGames}`
      );
      resultSelect.add(option);
    }
  }

  resultChanged(event) {
    // Clear any existing game scores first
    this.clearGameScores();
    
    const result = this.resultTarget.value;
    if (!result) {
      this.submitBtnTarget.classList.add('d-none');
      return;
    }
    
    // Show submit button when result is selected
    this.submitBtnTarget.classList.remove('d-none');
    
    // Add game score inputs based on result
    this.addGameScores(result);
  }

  clearGameScores() {
    const table = this.gameHeadersRowTarget.closest('table');
    const tbody = table.querySelector('tbody');
    const challengerRow = tbody.querySelector('tr:first-child');
    const challengedRow = tbody.querySelector('tr:last-child');
    const headerRow = this.gameHeadersRowTarget;
    
    // Remove game headers (keep first 2 columns: Player, Result)
    while (headerRow.children.length > 2) {
      headerRow.lastChild.remove();
    }
    
    // Remove game score cells from challenger row
    // Challenger row has 2 cells initially (name cell + result cell that spans 2 rows)
    while (challengerRow.children.length > 2) {
      challengerRow.lastChild.remove();
    }
    
    // Remove game score cells from challenged row  
    // Challenged row has only 1 cell initially (name cell, no result cell)
    while (challengedRow.children.length > 1) {
      challengedRow.lastChild.remove();
    }
  }

  addGameScores(result) {
    // Parse result (e.g., "A3" means challenger wins 3-0)
    const winner = result.charAt(0); // 'A' for challenger, 'B' for challenged
    const totalGames = parseInt(result.substring(1));
    
    // Use maxGames value if available, default to 5 (best of 5)
    const maxGames = this.hasMaxGamesValue ? this.maxGamesValue : 5;
    const winnerGames = Math.ceil((maxGames + 1) / 2); // Games needed to win
    const loserGames = totalGames - winnerGames;
    
    // Determine who wins each game
    const gameResults = [];
    const loserSide = winner === 'A' ? 'B' : 'A';
    
    // Add loser's games first
    for (let i = 0; i < loserGames; i++) {
      gameResults.push(loserSide);
    }
    
    // Add winner's games
    for (let i = 0; i < winnerGames; i++) {
      gameResults.push(winner);
    }
    
    // Get the table element and find tbody rows
    const table = this.gameHeadersRowTarget.closest('table');
    const tbody = table.querySelector('tbody');
    const challengerRow = tbody.querySelector('tr:first-child');
    const challengedRow = tbody.querySelector('tr:last-child');
    
    // Add game headers
    const headerRow = this.gameHeadersRowTarget;
    for (let i = 0; i < totalGames; i++) {
      const gameHeader = document.createElement('th');
      gameHeader.className = 'text-center';
      gameHeader.textContent = `Game ${i + 1}`;
      headerRow.appendChild(gameHeader);
    }
    
    // Add score inputs for each game
    for (let i = 0; i < totalGames; i++) {
      const gameNumber = i;
      const gameWinner = gameResults[i];
      
      // Default scores based on sport configuration
      const winnerScore = this.defaultWinnerScoreValue;
      const loserScore = this.defaultLoserScoreValue;
      let challengerScore, challengedScore;
      if (gameWinner === 'A') {
        challengerScore = winnerScore;
        challengedScore = loserScore;
      } else {
        challengerScore = loserScore;
        challengedScore = winnerScore;
      }
      
      // Challenger score cell
      const challengerCell = document.createElement('td');
      challengerCell.className = 'text-center';
      challengerCell.appendChild(this.createScoreInput(gameNumber, 'a', challengerScore));
      challengerRow.appendChild(challengerCell);
      
      // Challenged score cell
      const challengedCell = document.createElement('td');
      challengedCell.className = 'text-center';
      challengedCell.appendChild(this.createScoreInput(gameNumber, 'b', challengedScore));
      challengedRow.appendChild(challengedCell);
    }
  }

  createScoreInput(gameNumber, side, defaultValue) {
    const wrapper = document.createElement('div');
    wrapper.className = 'd-flex justify-content-center';
    
    const input = document.createElement('input');
    input.type = 'number';
    input.name = `games[${gameNumber}][side_${side}_score]`;
    input.className = 'form-control form-control-sm text-center';
    input.style.width = '60px';
    input.min = '0';
    input.max = '99';
    input.value = defaultValue;
    
    wrapper.appendChild(input);
    
    return wrapper;
  }
}